import styled from 'styled-components';
import {graphql} from 'gatsby';
import React, {FC} from 'react';

import quoteOpen from '../../../assets/images/quoteOpen.svg';
import {
  ImageTextDescButton as ImageTextDescButtonComponent,
  ImageTextDescButtonCssProps,
  getCssProps as imageTextDescButtonComponentGetCssProps,
  ImageTextDescButtonProps
} from '../../../components/modules/imageTextBlocks/descButton';

import {
  ImageTextDescButton as ImageTextDescButtonR4Component,
  ImageTextDescButtonCssProps as ImageTextDescButtonR4CssProps,
  getCssProps as imageTextDescButtonR4ComponentGetCssProps
} from '../../../components/modules/imageTextBlocks/descButtonR4';

import {ImageTopTextDescButton} from '../../../components/modules/imageTextBlocks/topCentered';
import {TitleDesc} from '../../../components/modules/imageTextBlocks/titleDesc';
import {ImageTextDescButtonFragment} from '../../../lib/queryTypes';
import {RichText, RichTextBlockType} from '../richText';
import {mediaBreakpointDown, fontSizesMixins} from '../../../lib/styleVariables';
import {StylableProps} from '../../../lib/styleHelpers';
import {transformFluidProps} from '../image';
import {IconProps} from '../../../components/molecules/icon';
import {StyledGradientBulletLi, StyledGradientBulletUl} from './descButton/gradient-bullet';

const StyledBlockquote = styled.blockquote`
  margin: 40px 0 0;
  padding-top: 16px;
  position: relative;

  p {
    margin-bottom: 0;

    &:not(:first-child) {
      &:not(:last-child) {
        margin-top: 24px;
      }
    }

    &:last-child {
      ${fontSizesMixins.text4()};
      margin-top: 2px;
    }

    &:first-child {
      text-indent: 30px;
    }
  }

  &::after {
    width: 48px;
    height: 1px;
    background: currentColor;
    content: '';
    position: absolute;
    top: 0;
    left: 0;
  }

  &::before {
    color: black;
    width: 18px;
    height: 32px;
    background: url(${quoteOpen}) no-repeat center / contain;
    content: '';
    position: absolute;
    top: 14px;
    left: 0;
  }

  ${mediaBreakpointDown('mobile')} {
    margin-top: 24px;
  }
`;

const StyledP = styled.p`
  margin: 0;
`;

interface DynamicProps {
  shouldRenderInboxAnalysisBanner: boolean | null;
  isImageClickable?: boolean;
}

export const ImageTextDescButton: FC<ImageTextDescButtonFragment &
  ImageTextDescButtonCssProps &
  ImageTextDescButtonR4CssProps &
  DynamicProps &
  StylableProps> = props => {
    if (!props.media)
      return (
        <TitleDesc
          description={props.content && <RichText document={props.content!.json} />}
          title={
            props.title && (
              <RichText
                document={props.title!.json}
                blockOverrides={{
                  [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                }}
              />
            )
          }
        />
      );

    switch (props.mediaVerticalPosition && props.mediaVerticalPosition!.toLowerCase()) {
      case 'media on top':
        return (
          <ImageTopTextDescButton
            description={props.content && <RichText document={props.content!.json!} />}
            imgSrc={transformFluidProps(props.media!.fluid!)}
            primaryButtonText={props.primaryButtonText!}
            primaryButtonLink={props.primaryButtonLink!}
            secondaryButtonText={props.secondaryButtonText!}
            secondaryButtonLink={props.secondaryButtonLink!}
            title={
              props.title && (
                <RichText
                  document={props.title!.json}
                  blockOverrides={{
                    [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
                  }}
                />
              )
            }
          />
        );
      case 'media on side':
      default: {
        const isGradientBullet = props.moduleMetadata?.useGradientBullet!;

        const gradientBulletBlocks = {
          [RichTextBlockType.LIST_ITEM]: (node: any, children: any) => (
            <StyledGradientBulletLi>{children}</StyledGradientBulletLi>
          ),
          [RichTextBlockType.UL_LIST]: (node: any, children: any) => (
            <StyledGradientBulletUl>{children}</StyledGradientBulletUl>
          )
        };

        const commonProps: Omit<ImageTextDescButtonProps, 'isHighlighted'> = {
          className: props.className,
          category: props.category!,
          title: props.title && (
            <RichText
              document={props.title!.json}
              blockOverrides={{
                [RichTextBlockType.PARAGRAPH]: (node: any, children: any) => <StyledP>{children}</StyledP>
              }}
            />
          ),
          content: props.content && (
            <RichText
              document={props.content!.json}
              blockOverrides={{
                [RichTextBlockType.QUOTE]: (node: any, children: any) => (
                  <StyledBlockquote>{children}</StyledBlockquote>
                ),
                ...(isGradientBullet ? gradientBulletBlocks : {})
              }}
            />
          ),
          primaryButtonText: props.primaryButtonText!,
          primaryButtonLink: props.primaryButtonLink!,
          secondaryButtonText: props.secondaryButtonText!,
          secondaryButtonLink: props.secondaryButtonLink!,
          tertiaryButtonText: props.tertiaryButtonText!,
          tertiaryButtonLink: props.tertiaryButtonLink!,
          videoSrc:
          props.media!.file!.contentType && props.media!.file!.contentType!.indexOf('video') > -1
            ? props.media!.file!.url!
            : undefined,
          imgSrc: props.media?.fluid! ? transformFluidProps(props.media!.fluid!) : undefined,
          mediaType:
          props.media!.file!.contentType && props.media!.file!.contentType!.indexOf('image/') > -1
            ? 'image'
            : 'video',
          mediaPosition: props.mediaPosition && props.mediaPosition.toLowerCase() === 'left' ? 'left' : 'right',
          isImageClickable: props.isImageClickable
        };
        if (props.designVersion === 'r4')
          return (
            <ImageTextDescButtonR4Component
              {...imageTextDescButtonR4ComponentGetCssProps(props)}
              {...commonProps}
              topIconName={props.topIconName as IconProps['name']}
              isCenteredTitle={props.isCenteredTitle!}
              titleColor={props.moduleMetadata?.titleColor!}
              useSmallIcon={props.moduleMetadata?.useSmallIcon!}
              footnote={props.moduleMetadata?.footnote!}
            />
          );

        return (
          <ImageTextDescButtonComponent
            {...imageTextDescButtonComponentGetCssProps(props)}
            {...commonProps}
            isHighlighted={props.isHighlighted!}
          />
        );
      }
    }
  };

export const query = graphql`
  fragment ImageTextDescButton on ContentfulModulesImageTextLarge {
    category
    title {
      json
    }
    content {
      json
    }
    media {
      file {
        contentType
        url
      }
      ...ImageContentfulLargeFluid
    }
    designVersion
    topIconName
    primaryButtonText
    primaryButtonLink
    secondaryButtonText
    secondaryButtonLink
    tertiaryButtonText
    tertiaryButtonLink
    mediaPosition
    mediaVerticalPosition
    isHighlighted
    isCenteredTitle
    moduleMetadata {
      titleColor
      backgroundColor
      useSmallIcon
      useGradientBullet
      footnote
    }
    shouldRenderInboxAnalysisBanner
  }
`;
